import { useContext, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGlobalKeybinding } from '../hooks/keybinding/useKeybinding.js';
import { createAppKeybindings } from '../hooks/keybinding/appKeybindings.js';
import { AppContext } from './AppContext.tsx';
import { isElectron } from '../utils/platform.js';

/**
 * AppKeybindings - Component that registers global application keybindings
 * 
 * This component must be inside the KeybindingProvider to access the context.
 * It registers all the global app shortcuts and returns null (no visual output).
 */
const AppKeybindings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    setSearchModal,
    focusMainInput,
    toggleModelSelector,
    openFileInput,
    openThinkingModeSelector,
    createNewConversation,
    toggleSidebar,
    selectedConversation,
    favoriteConversation,
    deleteConversation,
    showConfirmation,
    // Tab management
    tabs,
    activeTabId,
    getActiveTab,
    switchTab,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    createTab,
  } = useContext(AppContext);

  // Use refs to avoid re-registration on state changes during streaming
  // In Electron mode with tabs, we need to track the active tab's conversation
  // In browser mode, we use the global selectedConversation
  const selectedConversationRef = useRef(selectedConversation);
  const tabsRef = useRef(tabs);
  const activeTabIdRef = useRef(activeTabId);
  const getActiveTabRef = useRef(getActiveTab);

  // Keep refs updated
  useEffect(() => {
    // In Electron with tabs, use active tab's conversation ID
    // In browser, use global selectedConversation
    if (isElectron() && activeTabId) {
      const activeTab = getActiveTab();
      if (activeTab?.conversationId) {
        selectedConversationRef.current = {
          conversationId: activeTab.conversationId,
          title: activeTab.title || '',
          history: [],
          favorite: false
        };
      } else {
        selectedConversationRef.current = { conversationId: null, title: '', history: [], favorite: false };
      }
    } else {
      selectedConversationRef.current = selectedConversation;
    }
  }, [selectedConversation, activeTabId, getActiveTab]);

  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  useEffect(() => {
    activeTabIdRef.current = activeTabId;
  }, [activeTabId]);

  useEffect(() => {
    getActiveTabRef.current = getActiveTab;
  }, [getActiveTab]);

  // Create keybindings - recreate when location changes for context awareness
  const appKeybindings = useMemo(() => createAppKeybindings({
    toggleSidebar,
    setSearchModal,
    toggleModelSelector,
    openFileInput,
    openThinkingModeSelector,
    createNewConversation,
    focusMainInput,
    favoriteConversation,
    deleteConversation,
    showConfirmation,
  }, navigate, selectedConversationRef, location), [
    toggleSidebar,
    setSearchModal,
    toggleModelSelector,
    openFileInput,
    openThinkingModeSelector,
    createNewConversation,
    focusMainInput,
    favoriteConversation,
    deleteConversation,
    showConfirmation,
    navigate,
    location
  ]);
  
  // Register keybindings only once - no need to re-register for conversation changes
  useGlobalKeybinding(appKeybindings);

  // Handle Electron menu actions (⌘T, ⌘W, ⌘1-9 are intercepted by native menu)
  // Uses refs for tabs/activeTabId to avoid re-registering the IPC listener on every state change
  useEffect(() => {
    if (!window.electronAPI?.onMenuAction) {
      return;
    }

    const handleMenuAction = (action) => {
      // Handle switch-tab-N actions
      if (action.startsWith('switch-tab-')) {
        const tabNum = parseInt(action.replace('switch-tab-', ''), 10);
        const currentTabs = tabsRef.current;
        if (currentTabs && currentTabs.length >= tabNum) {
          const sortedTabs = [...currentTabs].sort((a, b) => a.createdAt - b.createdAt);
          const targetTab = sortedTabs[tabNum - 1];
          if (targetTab) {
            switchTab(targetTab.id);
          }
        }
        return;
      }

      switch (action) {
        case 'new-tab':
          createTab();
          break;
        case 'close-tab':
          if (tabsRef.current && tabsRef.current.length > 1 && activeTabIdRef.current) {
            closeTab(activeTabIdRef.current);
          } else {
            window.electronAPI?.closeWindow?.();
          }
          break;
        case 'close-other-tabs':
          if (activeTabIdRef.current) {
            closeOtherTabs(activeTabIdRef.current);
          }
          break;
        case 'close-all-tabs':
          closeAllTabs();
          break;
        case 'new-conversation':
          createNewConversation();
          break;
      }
    };

    window.electronAPI.onMenuAction(handleMenuAction);

    return () => {
      window.electronAPI.removeMenuActionListener?.();
    };
  }, [createTab, closeTab, closeOtherTabs, closeAllTabs, switchTab, createNewConversation]);

  // This component renders nothing - it just registers keybindings
  return null;
};

export default AppKeybindings;